import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, CalendarDays, FileText, IndianRupee, Send, Loader2 } from 'lucide-react';
import PageHero from '@/components/PageHero';
import { Reveal, SectionHeading } from '@/components/Reveal';
import { SCHOOL, ADMISSION_STEPS, ADMISSION_DOCUMENTS, IMPORTANT_DATES, FEE_NOTE } from '@/data/content';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CLASSES = ['Nursery', 'LKG', 'UKG', 'Class I', 'Class II', 'Class III', 'Class IV', 'Class V', 'Class VI', 'Class VII', 'Class VIII', 'Class IX'];

const inputCls = 'w-full rounded-lg border border-navy/15 bg-white px-4 py-3 text-sm text-navy placeholder:text-navy/40 focus:border-crimson focus:outline-none focus:ring-2 focus:ring-crimson/20 transition';

const EnquiryForm = () => {
  const [form, setForm] = useState({ student_name: '', parent_name: '', phone: '', email: '', class_applying: '', message: '', website: '' });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form, email: form.email || undefined, message: form.message || undefined };
      const res = await axios.post(`${API}/admission-enquiry`, payload);
      toast.success(res.data.message || 'Enquiry submitted successfully!');
      setForm({ student_name: '', parent_name: '', phone: '', email: '', class_applying: '', message: '', website: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail?.[0]?.msg || 'Could not submit. Please call us directly.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="admission-enquiry-form" className="rounded-xl border border-navy/10 bg-white p-6 sm:p-10 shadow-sm">
      <h3 className="font-display text-2xl font-bold text-navy">Online Admission Enquiry</h3>
      <p className="mt-2 text-sm text-navy/60">Session {SCHOOL.session} · Our team will call you within 24 hours.</p>
      <input type="text" name="website" value={form.website} onChange={set('website')} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
      <div className="mt-8 grid gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="student-name" className="mb-1.5 block text-xs font-bold uppercase tracking-[0.15em] text-navy/60">Student Name *</label>
          <input id="student-name" data-testid="enquiry-student-name" required minLength={2} value={form.student_name} onChange={set('student_name')} className={inputCls} placeholder="Child's full name" />
        </div>
        <div>
          <label htmlFor="parent-name" className="mb-1.5 block text-xs font-bold uppercase tracking-[0.15em] text-navy/60">Parent Name *</label>
          <input id="parent-name" data-testid="enquiry-parent-name" required minLength={2} value={form.parent_name} onChange={set('parent_name')} className={inputCls} placeholder="Father / Mother name" />
        </div>
        <div>
          <label htmlFor="phone" className="mb-1.5 block text-xs font-bold uppercase tracking-[0.15em] text-navy/60">Mobile Number *</label>
          <input id="phone" data-testid="enquiry-phone" required minLength={8} maxLength={15} value={form.phone} onChange={set('phone')} className={inputCls} placeholder="10-digit mobile number" />
        </div>
        <div>
          <label htmlFor="email" className="mb-1.5 block text-xs font-bold uppercase tracking-[0.15em] text-navy/60">Email (optional)</label>
          <input id="email" data-testid="enquiry-email" type="email" value={form.email} onChange={set('email')} className={inputCls} placeholder="you@example.com" />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="class-applying" className="mb-1.5 block text-xs font-bold uppercase tracking-[0.15em] text-navy/60">Class Applying For *</label>
          <select id="class-applying" data-testid="enquiry-class" required value={form.class_applying} onChange={set('class_applying')} className={inputCls}>
            <option value="">Select class</option>
            {CLASSES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="message" className="mb-1.5 block text-xs font-bold uppercase tracking-[0.15em] text-navy/60">Message (optional)</label>
          <textarea id="message" data-testid="enquiry-message" rows={3} value={form.message} onChange={set('message')} className={inputCls} placeholder="Any question about admission, transport, fees..." />
        </div>
      </div>
      <button type="submit" data-testid="enquiry-submit" disabled={loading} className="mt-8 inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-full bg-crimson px-8 py-3.5 text-sm font-bold text-white transition-all hover:bg-crimson-dark hover:-translate-y-0.5 disabled:opacity-60 disabled:translate-y-0">
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        {loading ? 'Submitting...' : 'Submit Enquiry'}
      </button>
    </form>
  );
};

const Admissions = () => (
  <>
    <PageHero eyebrow="Admissions" title={`Admissions Open · ${SCHOOL.session}`} description="Give your child the best start for a brighter tomorrow. Simple process, honest guidance, affordable fees — Nursery to Class IX." />

    <section className="py-24 sm:py-32" data-testid="admission-steps">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading number="01" eyebrow="Admission Process" title="Four simple steps to join Ideal" />
        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {ADMISSION_STEPS.map((s, i) => (
            <Reveal key={s.step} delay={i * 0.08}>
              <div data-testid={`admission-step-${i}`} className="relative h-full rounded-lg border border-navy/10 bg-white p-7 pt-10 transition-all hover:-translate-y-1 hover:border-gold">
                <span className="absolute -top-5 left-6 font-display text-5xl font-black text-gold">{s.step}</span>
                <h3 className="mt-4 font-display text-xl font-bold text-navy">{s.title}</h3>
                <p className="mt-3 text-sm text-navy/70 leading-relaxed">{s.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="bg-cream-dark py-24 sm:py-32" data-testid="eligibility-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid gap-12 lg:grid-cols-2">
        <div>
          <SectionHeading number="02" eyebrow="Eligibility Criteria" title="Who can apply" />
          <Reveal delay={0.15}>
            <ul className="mt-8 space-y-4">
              {[
                'Nursery: child should be 3+ years as on 31 March of the session year.',
                'Class I: child should be 6+ years as on 31 March of the session year.',
                'Class II onwards: admission based on previous class result and a short interaction.',
                'No donation, no capitation fee — admission purely on interaction and seat availability.',
              ].map((t, i) => (
                <li key={i} data-testid={`eligibility-${i}`} className="flex gap-3 rounded-lg bg-white border border-navy/10 p-4 text-sm text-navy/80 leading-relaxed">
                  <CheckCircle2 className="h-5 w-5 text-crimson shrink-0 mt-0.5" /> {t}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
        <div>
          <SectionHeading number="03" eyebrow="Required Documents" title="Keep these ready" />
          <Reveal delay={0.15}>
            <ul className="mt-8 space-y-3">
              {ADMISSION_DOCUMENTS.map((d, i) => (
                <li key={d} data-testid={`document-${i}`} className="flex items-center gap-3 rounded-lg bg-white border border-navy/10 px-4 py-3.5 text-sm font-medium text-navy/80">
                  <FileText className="h-4 w-4 text-gold shrink-0" /> {d}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </div>
    </section>

    <section className="py-24 sm:py-32" data-testid="fees-dates-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid gap-10 lg:grid-cols-2">
        <Reveal>
          <div className="h-full rounded-xl bg-navy p-8 sm:p-10 text-cream noise-overlay">
            <IndianRupee className="h-8 w-8 text-gold" />
            <h3 className="mt-5 font-display text-2xl font-bold">Fee Information</h3>
            <p className="mt-4 text-cream/70 leading-relaxed">{FEE_NOTE}</p>
            <a href={`tel:${SCHOOL.phones[0].replace(/\s/g, '')}`} data-testid="fee-call-link" className="mt-6 inline-flex items-center gap-2 rounded-full bg-gold px-6 py-3 text-sm font-bold text-navy transition-all hover:-translate-y-0.5">
              Call for fee details · {SCHOOL.phones[0]}
            </a>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="h-full rounded-xl border border-navy/10 bg-white p-8 sm:p-10">
            <CalendarDays className="h-8 w-8 text-crimson" />
            <h3 className="mt-5 font-display text-2xl font-bold text-navy">Important Dates</h3>
            <ul className="mt-6 divide-y divide-navy/10">
              {IMPORTANT_DATES.map((d, i) => (
                <li key={i} data-testid={`important-date-${i}`} className="flex items-center justify-between gap-4 py-4">
                  <span className="text-sm font-semibold text-navy/70">{d.label}</span>
                  <span className="rounded-full bg-gold-light px-3 py-1 text-xs font-bold text-navy">{d.date}</span>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      </div>
    </section>

    <section className="bg-cream-dark py-24 sm:py-32" data-testid="enquiry-form-section">
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <SectionHeading number="04" eyebrow="Apply Online" title="Start your child's journey today" />
        <div className="mt-12">
          <Reveal delay={0.15}><EnquiryForm /></Reveal>
        </div>
      </div>
    </section>
  </>
);

export default Admissions;
